if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      const variantInput = this.form?.querySelector('[name=id]');
      if (variantInput) variantInput.disabled = false;

      this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');

      if (document.querySelector('cart-drawer')) {
        this.submitButton?.setAttribute('aria-haspopup', 'dialog');
      }

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.error = false;
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      if (
        !this.form ||
        !this.submitButton ||
        this.submitButton.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      this.handleErrorMessage();
      this.error = false;

      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);

      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then(async (response) => {
          let payload = {};

          try {
            payload = await response.json();
          } catch (error) {
            payload = {};
          }

          if (!response.ok && !payload.status) {
            const fallbackMessage =
              window.cartStrings?.error ||
              'There was a problem adding this item to the cart.';

            payload.status = response.status || 500;
            payload.message = payload.message || fallbackMessage;
            payload.description = payload.description || fallbackMessage;
          }

          return payload;
        })
        .then((response) => {
          if (response.status) {
            const message =
              response.description ||
              response.message ||
              window.cartStrings?.error ||
              'There was a problem adding this item to the cart.';

            document.dispatchEvent(
              new CustomEvent('cart:error', {
                detail: {
                  source: this.dataset.source,
                  productVariantId: formData.get('id'),
                  errors: message,
                  message: response.message || message,
                },
              })
            );

            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: message,
              message: response.message || message,
            });

            this.handleErrorMessage(message);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            this.error = Boolean(soldOutMessage && Number(response.status) === 422);

            if (this.error) {
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButton.querySelector('span')?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            return;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          document.dispatchEvent(
            new CustomEvent('variant:add', {
              detail: {
                variant: {
                  id: formData.get('id'),
                },
                quantity: Number(formData.get('quantity') || 1),
                formElement: this.form,
                sectionId: this.dataset.source,
              },
            })
          );

          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => this.completeSuccessfulAdd(response, formData),
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.completeSuccessfulAdd(response, formData);
          }
        })
        .catch((error) => {
          this.error = false;

          const message =
            window.cartStrings?.error ||
            'There was a problem adding this item to the cart.';

          console.error('[ProductForm] Add to cart request failed:', error);
          this.handleErrorMessage(message);

          document.dispatchEvent(
            new CustomEvent('cart:error', {
              detail: {
                source: this.dataset.source,
                productVariantId: formData.get('id'),
                errors: message,
                message,
              },
            })
          );

          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: message,
            message,
          });
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');

          if (this.cart?.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }

          if (!this.error) {
            this.submitButton.removeAttribute('aria-disabled');
          }
        });
    }

    completeSuccessfulAdd(response, formData) {
      try {
        this.cart.renderContents(response);
      } catch (error) {
        console.error('[ProductForm] Cart drawer render failed, falling back to cart page:', error);
        window.location = window.routes.cart_url;
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: formData.get('id'),
      });

      this.scheduleFullCartSync();
    }

    scheduleFullCartSync() {
      const sync = () => {
        fetch(`${routes.cart_url}.js`, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Cart state request failed with ${response.status}`);
            }
            return response.json();
          })
          .then((cart) => {
            document.dispatchEvent(
              new CustomEvent('cart:change', {
                detail: {
                  cart,
                  sectionId: this.dataset.source,
                },
              })
            );
          })
          .catch((error) => {
            console.error('[ProductForm] Deferred cart state sync failed:', error);
          });
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(sync, { timeout: 650 });
      } else {
        window.setTimeout(sync, 120);
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper =
        this.errorMessageWrapper ||
        this.querySelector('.product-form__error-message-wrapper');

      if (!this.errorMessageWrapper) return;

      this.errorMessage =
        this.errorMessage ||
        this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
